import { create } from 'zustand';
import { BRAND_NAME, WHATSAPP_NUMBER, WHATSAPP_URL } from '../lib/site';
import { useCookieBanner } from './CookieBanner';

function Icon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.08-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.2-.24-.58-.48-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.06 2.86 1.21 3.06c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35Z" />
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 0 0 4.76 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.17 8.17 0 0 1-1.25-4.36c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.25 8.23Z" />
    </svg>
  );
}

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * En escritorio, wa.me redirige a una pantalla intermedia de api.whatsapp.com
 * («Abrir aplicación / Continuar en WhatsApp Web») que no aporta nada:
 * web.whatsapp.com/send abre el chat directamente. En móvil sí vale wa.me,
 * porque entrega la conversación a la app.
 */
const waLink = (text: string) =>
  isMobile()
    ? `${WHATSAPP_URL}?text=${encodeURIComponent(text)}`
    : `https://web.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(text)}`;

const DEFAULT_TEXT = 'Hola, me gustaría información sobre vuestros productos';

/**
 * El panel vive en el FAB, pero lo abren también las fichas de producto —con el
 * mensaje ya redactado—, así que el estado sale del componente a un store.
 */
const usePanel = create<{
  open: boolean;
  text: string;
  show: (text?: string) => void;
  hide: () => void;
  setText: (text: string) => void;
}>((set) => ({
  open: false,
  text: '',
  show: (text = '') => set({ open: true, text }),
  hide: () => set({ open: false }),
  setText: (text) => set({ text }),
}));

/**
 * Acceso permanente a WhatsApp, esquina inferior derecha de todas las páginas.
 *
 * El panel que se despliega redacta el mensaje dentro de la web; el envío
 * sí abre WhatsApp, porque web.whatsapp.com prohíbe cargarse en un iframe
 * (X-Frame-Options) y no existe un widget de chat embebible sin la API de
 * WhatsApp Business y un proveedor externo.
 */
export default function WhatsAppFab() {
  const { open, text, show, hide, setText } = usePanel();
  const cookieBanner = useCookieBanner((s) => s.visible);
  const href = waLink(text.trim() || DEFAULT_TEXT);

  return (
    // z-40: por debajo del aviso de cookies (z-50), que también vive en esta esquina
    // y desaparece en cuanto el visitante elige.
    // ponytail: el desplazamiento mientras el aviso de cookies ocupa esta esquina es
    // un valor fijo medido a ojo; si el texto del aviso crece, medir su alto real.
    <div
      className={`fixed right-6 z-40 flex flex-col items-end gap-3 transition-[bottom] duration-300 ${
        cookieBanner ? 'bottom-[14.5rem]' : 'bottom-6'
      }`}
      onKeyDown={(e) => e.key === 'Escape' && hide()}
    >
      {open && (
        <div
          role="dialog"
          aria-label="Chat de WhatsApp"
          // El alto máximo salva el móvil en horizontal: el panel crece hacia arriba
          // y sin tope se saldría por encima de la pantalla.
          className="max-h-[calc(100vh-7rem)] w-[min(20rem,calc(100vw-3rem))] overflow-y-auto rounded-2xl border border-ink-600 bg-ink-900 shadow-elev"
        >
          <div className="flex items-center gap-2 bg-[#25D366] px-4 py-3 text-ink-950">
            <Icon className="h-6 w-6 shrink-0" />
            <p className="min-w-0 flex-1 text-sm font-semibold leading-tight">{BRAND_NAME}</p>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Abrir la conversación en WhatsApp"
              title="Abrir en WhatsApp"
            >
              {/* Flecha saliendo de una caja: el gesto universal de «se abre fuera». */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden className="h-4 w-4">
                <path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <button type="button" onClick={hide} aria-label="Cerrar chat" className="text-lg leading-none">
              ✕
            </button>
          </div>

          <div className="p-4">
            <p className="mb-3 rounded-xl rounded-tl-none bg-ink-800 p-3 text-sm text-bone-200">
              ¡Hola! Cuéntanos qué buscas y te pasamos nuestras mejores ofertas y precios.
            </p>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Escribe tu mensaje…"
              aria-label="Mensaje"
              // 16px en móvil: por debajo de eso, Safari de iOS hace zoom al enfocar.
              className="mb-3 w-full resize-none rounded-xl border border-ink-600 bg-ink-950 p-3 text-base sm:text-sm text-bone-100 placeholder:text-bone-400 focus:border-vapor-400 focus:outline-none"
            />
            <a
              href={href}
              // En móvil la propia app se queda con la navegación, así que abrir
              // en la misma pestaña evita dejar una en blanco detrás; en escritorio
              // sí abrimos aparte para no sacar a nadie de la tienda.
              target={isMobile() ? undefined : '_blank'}
              rel="noopener noreferrer"
              onClick={hide}
              className="btn-primary flex w-full items-center justify-center gap-2 !bg-[#25D366] !text-ink-950"
            >
              <Icon className="h-5 w-5" />
              Enviar
            </a>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => (open ? hide() : show())}
        aria-expanded={open}
        aria-label={open ? 'Cerrar chat de WhatsApp' : 'Abrir chat de WhatsApp'}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-ink-950 shadow-elev transition hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vapor-400"
      >
        <Icon className="h-7 w-7" />
      </button>
    </div>
  );
}

/** Reclamo dentro de la ficha de producto: ofertas y precios se negocian por WhatsApp. */
export function WhatsAppCta({ productName }: { productName: string }) {
  return (
    <div className="mb-8">
      <p className="text-xs text-[#25D366] uppercase tracking-widest mb-1.5 font-medium">
        Por WhatsApp te hacemos el mejor precio
      </p>
      <p className="text-sm text-bone-200 leading-relaxed mb-3">
        ¡Escríbenos y te informamos sin compromiso!
      </p>
      <button
        type="button"
        onClick={() => usePanel.getState().show(`Hola, me interesa ${productName}`)}
        className="btn-primary inline-flex items-center gap-2 !bg-[#25D366] !text-ink-950"
      >
        <Icon className="h-5 w-5" />
        WhatsApp
      </button>
    </div>
  );
}
